import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "../WidgetBreakpoint";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

import { Colours } from "@learningblocks/shared-components";

import { makeStyles } from "@material-ui/core/styles";
import Slider from "@material-ui/core/Slider";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";

import BarChart3 from "./BarChart3";

export default function Widget(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  // Include the widget providers
  const breakpoints = useBreakpoint();

  const [bins, setBins] = useState(8);
  const [display, setDisplay] = useState("histogram");
  const [loaded, setLoaded] = useState();

  const [widgetArea] = useState({
    height: 0,
    width: 0,
  });

  useEffect(() => {
    import(`./locales/${props.i18next.language}/translation.json`).then(
      (module) => {
        props.i18next.addResourceBundle(
          "en",
          "translation",
          module.default,
          true,
          true
        );
        setLoaded(true);
      }
    );
  }, []);

  const useStyles = makeStyles((theme) => ({
    root: {
      width: "calc(100% - 40px)",
      margin: "auto",
    },
    margin: {
      height: theme.spacing(3),
    },
  }));

  const muiTheme = createMuiTheme({
    overrides: {
      MuiSlider: {
        thumb: {
          height: "1px",
          width: "1px",
          color: "#ffaa27",
        },
        track: {
          height: "5px",
          color: "#ffaa27",
        },
        rail: {
          height: "5px",
          color: "white",
        },
        mark: {
          height: "8px",
          color: "white",
        },
        markLabel: {
          color: "white",
          fontSize: "12px",
          marginLeft: "1px",
        },
        markLabelActive: {
          color: "white",
        },
        valueLabel: {
          top: 7,
          left: -9,
          background: "#ffaa27",
          borderRadius: "10px",
          height: "20px",
          "& *": {
            marginTop: -4,
            marginLeft: 1,
            background: "transparent",
            color: "#003359",
          },
        },
      },
    },
  });

  function updateBins(value) {
    setBins(value);
  }

  const marks = [
    { value: 1, label: "1" },
    { value: 3, label: "3" },
    { value: 5, label: "5" },
    { value: 7, label: "7" },
    { value: 9, label: "9" },
    { value: 11, label: "11" },
    { value: 13, label: "13" },
    { value: 15, label: "15" },
    { value: 17, label: "17" },
    { value: 19, label: "19" },
  ];

  const classes = useStyles();

  const setDisplayTab = (tab) => {
    setDisplay(tab);
  };

  return (
    <>
      <div
        style={{
          display: props.intro === "" ? "none" : null,
          border: "1px solid white",
          borderRadius: "5px",
          padding: "20px",
          color: "white",
          margin: "20px",
        }}
      >
        {parse(
          t(
            "Both histograms below represent the same data. Any differences that you see between them are due to rounding the bin limits. Rounding those numbers might make it easier to communicate the data in specific regions of the graph. Sometimes, rounding bin limits might change the number of bins slightly. <br/>Thus, histograms involve subjective decisions. We can round bin widths or move bin starting points for easier communication. Rounding or moving to the nearest decimal, whole number, 10, or 100, etc. might make sense, depending on your data."
          )
        )}
      </div>
      <div
        className="widgetWrapper"
        style={{
          width: "100%",
          overflow: "hidden",
          display: loaded ? "flex" : "none",
          flexDirection: "column",
          // border: "1px solid red",
        }}
      >
        <div
          className="altTxt field"
          aria-label={t(
            "Two histograms with a sliding bar to adjust the number of bins are shown. The x axis shows the height range, and the y axis shows the count or frequency. The first histogram’s height ranges are not rounded up, while the second’s are rounded up to the nearest 0.25 or the nearest 0.5, or the nearest whole number, depending on the number of bins selected. Choosing more bins results in a more detailed view. Too few bins or too little detail and too many bins or too much detail can obscure your general impression of the data. Rounded bin limits make communication easier. As you drag the slider to the right, in both histograms the number of bins increases, the bin width decreases, and the frequency within a bin generally decreases. As you drag the slider to the left, the number of bins decreases, the bin width increases, and the frequency within a bin generally increases. The top histogram does not use rounding for the bin widths and it has bin limits that are more difficult to communicate. The histogram at the bottom does use rounding, and its bin limits are easier to communicate."
          )}
          style={{
            height: "0px",
            overflow: "hidden",
          }}
        >
          {t(
            "Two histograms with a sliding bar to adjust the number of bins are shown. The x axis shows the height range, and the y axis shows the count or frequency. The first histogram’s height ranges are not rounded up, while the second’s are rounded up to the nearest 0.25 or the nearest 0.5, or the nearest whole number, depending on the number of bins selected. Choosing more bins results in a more detailed view. Too few bins or too little detail and too many bins or too much detail can obscure your general impression of the data. Rounded bin limits make communication easier. As you drag the slider to the right, in both histograms the number of bins increases, the bin width decreases, and the frequency within a bin generally decreases. As you drag the slider to the left, the number of bins decreases, the bin width increases, and the frequency within a bin generally increases. The top histogram does not use rounding for the bin widths and it has bin limits that are more difficult to communicate. The histogram at the bottom does use rounding, and its bin limits are easier to communicate."
          )}
        </div>
        <div
          className="ctrl-title"
          style={{
            // fontWeight: "bold",
            width: "100%",
            textAlign: "center",
            color: "white",
            padding: "16px 0px 5px 0px",
          }}
        >
          {t("Choose the number of bins")}
        </div>
        <div
          className="chart-type-select"
          style={{
            background: "#002F52",
            maxWidth: "1000px",
            margin: "auto",
            border: "1px solid " + Colours.navy4,
            boxSizing: "border-box",
            borderRadius: "5px",
            display: widgetArea.height < 678 ? "flex" : "none",
            flexDirection: "row",
            width: "calc(100% - 20px)",
          }}
        >
          <div className={classes.root}>
            <ThemeProvider theme={muiTheme}>
              <Slider
                min={1}
                max={20}
                defaultValue={8}
                getAriaValueText={updateBins}
                aria-labelledby="discrete-slider-custom"
                step={1}
                marks={marks}
                valueLabelDisplay="on"
              />
            </ThemeProvider>
          </div>
        </div>
        <div
          // ref={widget.widgetArea}
          className="widgetCharts"
          style={{
            position: "relative",
            display: "flex",
            width: breakpoints.md ? "calc(100% - 32px)" : "calc(100% - 96px)",
            maxWidth: "1000px",
            height: "calc(100% - 32px)",
            padding: breakpoints.md ? "16px" : "32px",
            overflowY: "hidden",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "space-around",
            margin: "0 auto",
            // border: "1px solid red",
          }}
        >
          <BarChart3
            width={
              breakpoints.xs
                ? 200
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 500
                    : 500
            }
            height={
              breakpoints.xs
                ? 150
                : breakpoints.sm
                  ? 150
                  : breakpoints.md
                    ? 200
                    : 200
            }
            widgetData={props.widgetData}
            bins={bins}
            visible={props.visible}
            display={display}
            setDisplay={setDisplayTab}
          />
        </div>
      </div>
    </>
  );
}

Widget.propTypes = {
  widgetData: PropTypes.object,
  inline: PropTypes.boolean,
  visible: PropTypes.boolean,
  usersnap: PropTypes.string,
};
